import { debounce } from "lodash";
import axios from "../axios";
import { v4 as uuid } from "uuid";
import { LogEntry } from "dora-contracts";

const sessionId = uuid();

// Coalesce bursts of log events (e.g. many failing requests at once) into a single
// request instead of one POST per event, so a failure storm doesn't also exhaust
// the server's connections with log traffic.
const FLUSH_INTERVAL_MS = 2000;
const MAX_QUEUE_SIZE = 20;

let queue: LogEntry[] = [];

const flush = () => {
  if (queue.length === 0) return;
  const entries = queue;
  queue = [];
  axios.post("/api/log-entries/bulk", { entries }).catch(() => {
    // Don't throw error to avoid infinite circular loop
  });
};

const scheduleFlush = debounce(flush, FLUSH_INTERVAL_MS, {
  leading: false,
  trailing: true,
  maxWait: FLUSH_INTERVAL_MS,
});

const enqueue = (entry: LogEntry) => {
  queue.push({ ...entry, sessionId } as LogEntry);
  if (queue.length >= MAX_QUEUE_SIZE) {
    scheduleFlush.cancel();
    flush();
  } else {
    scheduleFlush();
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flush);
}

export const reportInfo = (body: Record<string, unknown>) => {
  enqueue({ ...body, level: "info" } as LogEntry);
};

export const formatError = (err: any) =>
  err instanceof Error
    ? `${err.toString()}\n${err.message}\n${err.stack}`
    : JSON.stringify(err);

export const reportError = (body: Record<string, unknown>) => {
  enqueue({ ...body, level: "error" } as LogEntry);
};

export const reportWarning = (body: Record<string, unknown>) => {
  enqueue({ ...body, level: "warning" } as LogEntry);
};
